import { isLoading } from "apollo/utils";
import {
  AHB_MESSAGE_STATUS,
  AHB_MESSAGE_TYPE,
  PATIENT_DOCUMENTS,
  RECARE_FILE_TYPE,
  REHAB_FORM_NAMES,
  TRACK_EVENTS,
} from "core/consts";
import { isAHBStatusRetrying } from "core/consts/typeGuards";
import { MAX_FILE_SIZE_AHB_MB } from "core/model/files/config";
import { activateDTAAttachments } from "core/model/utils/featureFlags";
import {
  AHBMessage,
  AHBMessagesPayload,
  AHBWithdrawFormValues,
  Auction,
  AuctionForm,
  FileState,
  Patient,
  QueryProgress,
  RehabFormName,
} from "core/types";
import { TrackingEventName } from "core/validationSchemas/tracking";
import { useConfirmationDialog } from "ds_legacy/components/ConfirmationDialog/useConfirmationDialog";
import { InfoChipProps } from "ds_legacy/components/InfoChip";
import { useToast } from "dsl/atoms/ToastNotificationContext";
import { getFileSize } from "dsl/ecosystems/PatientFiles/utils";
import useProductTour, { PRODUCT_TOURS } from "dsl/molecules/useProductTour";
import { getTranslateWithdrawReason } from "dsl/static/utils";
import { CircleHelpIcon } from "lucide-react";
import {
  AHBRequestProps,
  useGetXMLForMessageId,
  useSendAHBRequest,
  useWithdrawAHBRequest,
} from "onprem/hooks";
import {
  getMostRecentThread,
  groupMessagesByThread,
} from "onprem/messageThreadsUtils";
import { useCallback, useMemo } from "react";
import { useLoggedCareseeker } from "reduxentities/hooks";
import { useTranslations } from "translations";
import { useAHBNavigationPrevention } from "./useAHBNavigationPrevention";
import { useAHBResponseToHIS } from "./useAHBResponseToHIS";
import { useAHBResponseToPDF } from "./useAHBResponseToPDF";
import { getMedicalFormApproved } from "./utils";

export type AHBMessageSectionProps = {
  ahbMessagesPayload: AHBMessagesPayload | undefined;
  auction: Auction;
  auctionForms: AuctionForm[] | undefined;
  hasTwoWaySync: boolean;
  patient: Patient;
  queryProgress: QueryProgress;
  refetchAHBMessageThreads: () => Promise<void>;
};

export function getRehabFormGoToLinkTrackEvent(
  formName: RehabFormName,
): TrackingEventName {
  switch (formName) {
    case REHAB_FORM_NAMES.GKV_GENERAL:
      return TRACK_EVENTS.REHAB_FORM_GKV_GENERAL_GO_TO_LINK;
    case REHAB_FORM_NAMES.GKV_MEDICAL:
      return TRACK_EVENTS.REHAB_FORM_GKV_MEDICAL_GO_TO_LINK;
    case REHAB_FORM_NAMES.DRV_GENERAL:
      return TRACK_EVENTS.REHAB_FORM_DRV_GENERAL_GO_TO_LINK;
    case REHAB_FORM_NAMES.DRV_MEDICAL:
      return TRACK_EVENTS.REHAB_FORM_DRV_MEDICAL_GO_TO_LINK;
    default:
      return TRACK_EVENTS.REHAB_FORM_GO_TO_LINK;
  }
}

export function getRehabFormFillOutTrackEvent(
  formName: RehabFormName,
): TrackingEventName {
  switch (formName) {
    case REHAB_FORM_NAMES.GKV_GENERAL:
      return TRACK_EVENTS.REHAB_FORM_GKV_GENERAL_FILL_OUT;
    case REHAB_FORM_NAMES.GKV_MEDICAL:
      return TRACK_EVENTS.REHAB_FORM_GKV_MEDICAL_FILL_OUT;
    case REHAB_FORM_NAMES.DRV_GENERAL:
      return TRACK_EVENTS.REHAB_FORM_DRV_GENERAL_FILL_OUT;
    case REHAB_FORM_NAMES.DRV_MEDICAL:
      return TRACK_EVENTS.REHAB_FORM_DRV_MEDICAL_FILL_OUT;
    default:
      return TRACK_EVENTS.REHAB_FORM_GO_TO_LINK; // Fallback to existing event
  }
}

export function isMessageStatus(
  message: AHBMessage,
  status: keyof typeof AHB_MESSAGE_STATUS,
): message is AHBMessage & {
  status: (typeof AHB_MESSAGE_STATUS)[keyof typeof AHB_MESSAGE_STATUS];
} {
  return message.status === status;
}

export function useMissingIdentifiers({
  patient,
}: {
  patient: Patient;
}): string | undefined {
  const translations = useTranslations();
  const loggedCareseeker = useLoggedCareseeker();
  const insurance = patient.profile?.financing?.insurance;
  const labels = translations.patientForms.missingIdentifierLabels;

  const missingLabels: string[] = [];

  if (!insurance) {
    missingLabels.push(translations.patient.patientInformation.insurance);
  } else {
    if (!insurance.kim_address) missingLabels.push(labels.insuranceKimAddress);
    if (!insurance.ik_number) missingLabels.push(labels.insuranceIkNumber);
  }

  if (!loggedCareseeker?.kim_address)
    missingLabels.push(labels.hospitalKimAddress);
  if (!patient.careseeker?.company?.ik_numbers?.[0])
    missingLabels.push(labels.hospitalIkNumber);
  if (!patient.careseeker?.site_id_number)
    missingLabels.push(labels.siteIdNumber);

  if (missingLabels.length > 0) {
    return translations.patientForms.errorMissingIdentifiers({
      missingList: missingLabels.join(", "),
    });
  }

  return undefined;
}

export function useTranslateAHBMessageType() {
  const translations = useTranslations();

  return (message: AHBMessage) => {
    const { status, type } = message;

    switch (status) {
      case AHB_MESSAGE_STATUS.RETRYING: {
        return translations.insuranceApp.eligibilityTab.sendDocuments
          .historyFormRetrying;
      }

      case AHB_MESSAGE_STATUS.FAILED: {
        return translations.insuranceApp.eligibilityTab.sendDocuments
          .historyFormFailed;
      }

      case AHB_MESSAGE_STATUS.WITHDRAWN: {
        switch (type) {
          case AHB_MESSAGE_TYPE.REQUEST:
            return translations.insuranceApp.eligibilityTab.sendDocuments.historyFormFileSubmitted(
              { numberFiles: message.attached_file_count?.toString() ?? "0" },
            );
          default:
            return translations.insuranceApp.eligibilityTab.sendDocuments
              .historyFormWithdrawn;
        }
      }
      case AHB_MESSAGE_STATUS.DELIVERED: {
        switch (type) {
          case AHB_MESSAGE_TYPE.REQUEST:
            return translations.insuranceApp.eligibilityTab.sendDocuments.historyFormFileSubmitted(
              { numberFiles: message.attached_file_count?.toString() ?? "0" },
            );
          case AHB_MESSAGE_TYPE.WITHDRAWAL:
            return translations.insuranceApp.eligibilityTab.sendDocuments
              .historyFormWithdrawn;
          default:
            return translations.insuranceApp.eligibilityTab.sendDocuments
              .historyFormRetrying;
        }
      }

      default:
        return translations.insuranceApp.eligibilityTab.sendDocuments
          .historyFormRetrying;
    }
  };
}

export function useGetStatusChip() {
  const translations = useTranslations();

  return ({
    isApprovedByDoctor,
    isGKVFormCreated,
    isGKVMedicalForm,
  }: {
    isApprovedByDoctor: boolean | undefined;
    isGKVFormCreated: boolean;
    isGKVMedicalForm: boolean;
  }): { color: InfoChipProps["color"]; label: string } => {
    if (isGKVMedicalForm && isApprovedByDoctor) {
      return {
        color: "success",
        label:
          translations.insuranceApp.eligibilityTab.approvalFlow
            .statusChipApproved,
      };
    }

    if (isGKVFormCreated) {
      return {
        color: isGKVMedicalForm ? "warning" : "success",
        label: isGKVMedicalForm
          ? translations.insuranceApp.eligibilityTab.approvalFlow
              .statusChipPendingApproval
          : translations.insuranceApp.eligibilityTab.sendDocuments
              .statusChipComplete,
      };
    }

    return {
      color: undefined,
      label:
        translations.insuranceApp.eligibilityTab.sendDocuments
          .statusChipNotFilled,
    };
  };
}

export function useAHBMessageSection({
  ahbMessagesPayload,
  auction,
  auctionForms,
  files,
  patient,
  refetchAHBMessageThreads,
}: Pick<
  AHBMessageSectionProps,
  | "ahbMessagesPayload"
  | "auction"
  | "auctionForms"
  | "patient"
  | "refetchAHBMessageThreads"
> & {
  files?: FileState[];
}) {
  const toast = useToast();
  const translations = useTranslations();

  // Form state
  const isMedicalformApprovedByDoctor = getMedicalFormApproved(auctionForms);
  const isDRVFormCreated = !!auctionForms?.some(
    (form) =>
      form.form_type === RECARE_FILE_TYPE.DRV_GENERAL_FORM ||
      form.form_type === RECARE_FILE_TYPE.DRV_MEDICAL_FORM,
  );

  const isGKVMedicalFormCreated = !!auctionForms?.some(
    (form) => form.form_type === RECARE_FILE_TYPE.GKV_MEDICAL_FORM,
  );

  const isGKVGeneralFormCreated = !!auctionForms?.some(
    (form) => form.form_type === RECARE_FILE_TYPE.GKV_GENERAL_FORM,
  );

  // AHB request props
  const ahbRequestProps: AHBRequestProps = useMemo(
    () => ({
      auction,
      isApprovedByDoctor: isMedicalformApprovedByDoctor,
      patient,
    }),
    [auction, isMedicalformApprovedByDoctor, patient],
  );

  // Dialog states
  const [withdrawDialogProps, withdrawDialog] =
    useConfirmationDialog<AHBWithdrawFormValues>();
  const [confirmSendRequestProps, confirmSendRequest] = useConfirmationDialog();

  // API hooks
  const [sendProgress, sendAHBRequest] = useSendAHBRequest(ahbRequestProps);
  const [withdrawProgress, withdrawAHBRequest] =
    useWithdrawAHBRequest(ahbRequestProps);
  const [progress, renderAndOpenAHBPdf] = useAHBResponseToPDF();
  const [progress2, renderAndOpenAHBPdf2] = useAHBResponseToPDF();
  const [sendToHisProgress, sendToHis] = useAHBResponseToHIS({
    careseekerName: auction?.patient?.careseeker?.name ?? "",
    externalId: auction?.patient?.external_id ?? "",
    userId: auction?.patient?.user_id ?? "",
  });
  const [fetchXMLProgress, fetchXMLForMessageId] = useGetXMLForMessageId();

  // Message processing
  const ahbMessageThreads = useMemo(
    () => groupMessagesByThread(ahbMessagesPayload?.messages),
    [ahbMessagesPayload?.messages],
  );
  const responses = ahbMessagesPayload?.responses;
  const mostRecentThread = useMemo(
    () => getMostRecentThread(ahbMessagesPayload?.messages),
    [ahbMessagesPayload?.messages],
  );

  // Navigation prevention
  useAHBNavigationPrevention({
    fetchXMLProgress,
    progress,
    progress2,
    sendProgress,
    sendToHisProgress,
    withdrawProgress,
  });

  // Computed values
  const isLatestMessageFailedRequest =
    mostRecentThread?.message?.type === AHB_MESSAGE_TYPE.REQUEST &&
    mostRecentThread?.message?.status === AHB_MESSAGE_STATUS.FAILED;

  const isLatestMessageWithdrawal = !!mostRecentThread?.chain?.some(
    ({ type }) => type === AHB_MESSAGE_TYPE.WITHDRAWAL,
  );

  const totalFileSizeMB = useMemo(
    () => files?.reduce((total, file) => total + getFileSize(file), 0) ?? 0,
    [files],
  );

  const isFileSizeLimitExceeded = totalFileSizeMB > MAX_FILE_SIZE_AHB_MB;

  const canWithdrawMessage =
    !!mostRecentThread?.message &&
    isMessageStatus(mostRecentThread.message, AHB_MESSAGE_STATUS.DELIVERED) &&
    !mostRecentThread.chain.length;

  const shouldShowSendButton =
    !mostRecentThread?.message ||
    isAHBStatusRetrying(mostRecentThread?.message) ||
    isLatestMessageFailedRequest ||
    isLatestMessageWithdrawal;

  const isSendButtonDisabled =
    !isMedicalformApprovedByDoctor ||
    !isGKVGeneralFormCreated ||
    !isGKVMedicalFormCreated ||
    (activateDTAAttachments && isFileSizeLimitExceeded);

  const isFileDeleteDisabled =
    isAHBStatusRetrying(mostRecentThread?.message) ||
    isLoading(sendProgress) ||
    canWithdrawMessage;

  const isFileUploadDisabled = isFileDeleteDisabled;

  // Product tour
  useProductTour({
    skip:
      !!mostRecentThread || isGKVGeneralFormCreated || isGKVMedicalFormCreated,
    tour: PRODUCT_TOURS.dta,
  });

  // Event handlers
  const handleSendAHBRequest = async () => {
    if (!isMedicalformApprovedByDoctor) {
      return;
    }
    const insurance = patient.profile?.financing?.insurance;
    const kim_address_insurance = insurance?.kim_address;

    try {
      if (!kim_address_insurance) {
        throw new Error(
          "[handleSendAHBRequest] insurance kim_address is required",
        );
      }
      if (!auctionForms) {
        throw new Error("[handleSendAHBRequest] forms is a required prop");
      }

      confirmSendRequest({
        cancelText:
          translations.insuranceApp.eligibilityTab.sendDocuments.cancelButton,
        confirmButtonProps: {
          id: "send-ahb-request",
          trackEventProps: { insurance_name: insurance.name },
        },
        content:
          translations.insuranceApp.eligibilityTab.sendDocuments
            .confirmationSubtitle,
        headerIcon: (
          <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-primary-light">
            <CircleHelpIcon aria-hidden className="h-8 w-8 text-primary-dark" />
          </span>
        ),
        id: "send-ahb-request",
        onSubmit: async () => {
          const insurance = patient.profile?.financing?.insurance;
          const kim_address_insurance = insurance?.kim_address;
          const kim_address_hospital =
            auction?.patient?.careseeker?.kim_address;
          if (!kim_address_insurance || !kim_address_hospital) {
            throw new Error(
              "[handleSendAHBRequest] insurance kim_address and hospital kim_address is required",
            );
          }

          await sendAHBRequest({
            auctionForms,
            files,
          });
          refetchAHBMessageThreads();
        },
        submitText:
          translations.insuranceApp.eligibilityTab.sendDocuments.sendButton,
        title:
          translations.insuranceApp.eligibilityTab.sendDocuments.confirmationTitle(
            { insuranceName: insurance.name },
          ),
        useNewDesign: true,
      });
    } catch (err) {
      console.error("[handleSendAHBRequest]", (err as any).message);
      toast({ color: "danger", message: translations.general.error });
    }
  };

  const handleWithdrawAHBRequest = async (ahbMessage: AHBMessage) => {
    try {
      const formData = await withdrawDialog({ id: "withdraw-request" });
      if (!formData?.withdraw_reason) {
        return;
      }

      await withdrawAHBRequest({
        kim_address_hospital: ahbMessage.sender_kim,
        kim_address_insurance: ahbMessage.receiver_kim,
        request_message_id: ahbMessage.message_id,
        ...formData,
      });
      refetchAHBMessageThreads();
    } catch (err) {
      console.error("[handleSendAHBRequest]", (err as any).message);
      toast({ color: "danger", message: translations.general.error });
    }
  };

  const handleViewDocument = useCallback(
    (responseId: string) => {
      renderAndOpenAHBPdf(responseId);
    },
    [renderAndOpenAHBPdf],
  );

  const handlePrintDocument = useCallback(
    (responseId: string) => {
      renderAndOpenAHBPdf2(responseId);
    },
    [renderAndOpenAHBPdf2],
  );

  const handleSendToHis = useCallback(
    (responseId: string) => {
      sendToHis({
        documentType: PATIENT_DOCUMENTS.SEND_AHB_PDF_TO_HIS,
        response_id: responseId,
      });
    },
    [sendToHis],
  );

  const handleCopyXML = useCallback(
    (messageId: string) => {
      fetchXMLForMessageId(messageId);
    },
    [fetchXMLForMessageId],
  );

  return {
    ahbMessageThreads,
    canWithdrawMessage,
    confirmSendRequestProps,
    fetchXMLProgress,
    handleCopyXML,
    handlePrintDocument,
    handleSendAHBRequest,
    handleSendToHis,
    handleViewDocument,
    handleWithdrawAHBRequest,
    isDRVFormCreated,
    isFileDeleteDisabled,
    isFileSizeLimitExceeded,
    isFileUploadDisabled,
    isGKVGeneralFormCreated,
    isGKVMedicalFormCreated,
    isMedicalformApprovedByDoctor,
    isSendButtonDisabled,
    mostRecentThread,
    progress,
    progress2,
    responses,
    sendProgress,
    sendToHisProgress,
    shouldShowSendButton,
    totalFileSizeMB,
    withdrawDialogProps,
    withdrawProgress,
  };
}

export function useGetTranslateWithdrawReason() {
  const translations = useTranslations();
  return getTranslateWithdrawReason(translations);
}
